import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Input, Field, Badge, Spinner, Empty, RequireTenant } from "../ui";
import { AjutorButton } from "../AjutorButton";
import { useAuth, usePermissions } from "../AuthContext";
import { Users, Plus, Upload, Trash2, Search, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { new: "blue", clicked: "green", contacted: "amber", qualified: "green", unsubscribed: "red" };
const STATUS_LABEL = { new: "nou", clicked: "a dat clic", contacted: "contactat", qualified: "calificat", unsubscribed: "dezabonat" };

function AddContactModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", company: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/contacts", form);
      toast.success("Contact adaugat");
      onSaved();
    } catch (err) { toast.error(apiError(err, "Nu s-a putut adauga")); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Contact nou</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={save} data-testid="add-contact-form">
          <Field label="Email *"><Input data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Nume"><Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Companie"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
          <Field label="Telefon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 mt-2">
            <Btn variant="outline" type="button" onClick={onClose}>Anuleaza</Btn>
            <Btn type="submit" disabled={saving} data-testid="save-contact-button">Salveaza</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

function ContactsInner() {
  const { activeTenant } = useAuth();
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", { params: { search: q, limit: 200 } });
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [activeTenant]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Stergi acest contact?")) return;
    try { await api.delete(`/contacts/${id}`); toast.success("Sters"); load(search); }
    catch (err) { toast.error(apiError(err)); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import: ${data.imported} noi, ${data.duplicate_email + data.duplicate_domain} duplicate, ${data.invalid} invalide`);
      load(search);
    } catch (err) { toast.error(apiError(err, "Import esuat")); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const sources = Array.from(new Set(items.map((c) => c.source).filter(Boolean))).sort();
  const visible = sourceFilter ? items.filter((c) => c.source === sourceFilter) : items;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="contacts-search" placeholder="Cauta email, nume, companie…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(search)} className="pl-9" />
        </div>
        <select data-testid="contacts-source-filter" value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm outline-none">
          <option value="">Toate sursele</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={doImport} data-testid="import-file-input" />
        {can("contacte", "add") && <Btn variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-csv-button"><Upload className="w-4 h-4" /> Import CSV / Excel</Btn>}
        {can("contacte", "add") && <Btn onClick={() => setShowAdd(true)} data-testid="add-contact-button"><Plus className="w-4 h-4" /> Contact nou</Btn>}
      </div>

      <Card>
        {loading ? <Spinner /> : visible.length === 0 ? (
          <Empty icon={Users} title="Niciun contact" hint="Adauga manual, importa CSV sau primeste lead-uri din campanii." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Nume</th>
                  <th className="px-5 py-3 font-medium">Companie</th>
                  <th className="px-5 py-3 font-medium">Sursa</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actiuni</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`contact-row-${c.id}`}>
                    <td className="px-5 py-3 font-medium text-slate-900">{c.email}</td>
                    <td className="px-5 py-3 text-slate-600">{c.name || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{c.company || "—"}</td>
                    <td className="px-5 py-3 text-slate-600" data-testid={`contact-source-${c.id}`}>{c.source || "—"}</td>
                    <td className="px-5 py-3"><Badge color={STATUS_COLOR[c.status]}>{STATUS_LABEL[c.status] || c.status}</Badge></td>
                    <td className="px-5 py-3 text-right">
                      {can("contacte", "delete") && (
                        <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-contact-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <p className="text-xs text-slate-400 mt-3">{total} contacte in total</p>

      {showAdd && <AddContactModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(search); }} />}
    </div>
  );
}

export default function Contacts() {
  return (
    <div>
      <PageHeader title="Contacte" subtitle="Gestioneaza lead-urile si contactele companiei." />
      <AjutorButton file="ajutor_contacte.pdf" />
      <RequireTenant><ContactsInner /></RequireTenant>
    </div>
  );
}
